// pages/VocabularyExercise.jsx
// Page d'exercices de vocabulaire (traduction de noms, adjectifs, adverbes, pronoms, etc.)

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Loader2, CheckCircle, XCircle, TrendingUp, RotateCw, Play } from 'lucide-react';

// Import des utilitaires partagés
import { getLangueFlag, createInsertSpecialChar, getArticleDefini } from '../utils/exerciseUtils';
import VirtualKeyboard from '../components/VirtualKeyboard';
import { useLangueEtudiee } from '../hooks/useLangueEtudiee';

import { useAuth } from '../contexts/AuthContext';
import './ExerciseShared.css';
import './VocabularyExercise.css';

export default function VocabularyExercise() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id || 1; // Fallback userId=1 pour embed sans auth
  const langueEtudiee = useLangueEtudiee();
  const isEmbed = searchParams.get('embed') === 'true';

  // ========== ÉTATS ==========
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [error, setError] = useState(null);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showDontKnowModal, setShowDontKnowModal] = useState(false);
  const [dontKnowAnswer, setDontKnowAnswer] = useState('');

  // Charger les paramètres sauvegardés ou valeurs par défaut
  const savedParams = JSON.parse(localStorage.getItem('vocabularyParams') || '{}');

  // Filtres et options — langueEtudiee (param WP) prioritaire sur la préférence sauvegardée
  const [langueSource, setLangueSource] = useState(savedParams.langueSource || 'fr');
  const [langueCible, setLangueCible] = useState(langueEtudiee || savedParams.langueCible || 'en');
  const [typeFilter, setTypeFilter] = useState(savedParams.typeFilter || 'all');
  const [withArticle, setWithArticle] = useState(savedParams.withArticle || false);
  const [checkAccents, setCheckAccents] = useState(savedParams.checkAccents || false);

  // Source des mots (général, mes playlists, playlists publiques, catégories/thèmes)
  const [sourceType, setSourceType] = useState(savedParams.sourceType || 'general');
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(savedParams.selectedPlaylistId || null);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Catégories / thèmes
  const [categories, setCategories] = useState([]);
  const [selectedCategorieCode, setSelectedCategorieCode] = useState(savedParams.selectedCategorieCode || '');
  const [selectedThemeCode, setSelectedThemeCode] = useState(savedParams.selectedThemeCode || '');

  // Niveau CECRL (filtre global)
  const [niveauCecrl, setNiveauCecrl] = useState(savedParams.niveauCecrl || 'all');

  // Système de scoring et répétition espacée
  const [motScores, setMotScores] = useState({}); // { motId: score }
  const [retryQueue, setRetryQueue] = useState([]); // [{ motId, questionsUntilRetry, questionData }]
  const [seenMotIds, setSeenMotIds] = useState(new Set()); // Mots déjà vus dans cette session
  const [retrySameWord, setRetrySameWord] = useState(false); // Reproposer le même mot après "Je ne sais pas"

  // État pour afficher/masquer l'exercice
  const [showExercise, setShowExercise] = useState(false);

  // Réinitialiser l'écran 1 quand on navigue vers cette page depuis le menu
  useEffect(() => {
    if (location.state?.resetToScreen1) {
      setShowExercise(false);
      setQuestion(null);
      setUserAnswer('');
      setResult(null);
    }
  }, [location.state]);

  // Refs
  const exerciseRef = useRef(null);
  const inputRef = useRef(null);

  // ========== OPTIONS DE FILTRES ==========

  // Options de langue (sans 'all')
  const langueSourceOptions = [
    { value: 'fr', label: t('langues.fr'), flag: '🇫🇷' },
    { value: 'en', label: t('langues.en'), flag: '🇬🇧' },
    { value: 'es', label: t('langues.es'), flag: '🇪🇸' },
  ];

  // Options de langue cible (exclut la langue source)
  const langueCibleOptions = langueSourceOptions.filter((opt) => opt.value !== langueSource);

  // Reset langueCible si elle devient invalide (même que langueSource)
  useEffect(() => {
    if (langueCible === langueSource) {
      const newOptions = langueSourceOptions.filter((opt) => opt.value !== langueSource);
      if (newOptions.length > 0) {
        setLangueCible(newOptions[0].value);
      }
    }
  }, [langueSource, langueCible]);

  // Options de type de mot
  const typeOptions = [
    { value: 'all', label: t('vocabulary.typeAll') },
    { value: 'nom', label: t('vocabulary.typeNom') },
    { value: 'adjectif', label: t('vocabulary.typeAdjectif') },
    { value: 'adverbe', label: t('vocabulary.typeAdverbe') },
    { value: 'pronom', label: t('vocabulary.typePronom') },
    { value: 'adjectif_indefini', label: t('vocabulary.typeAdjectifIndefini') },
    { value: 'expression', label: t('vocabulary.typeExpression') },
  ];

  // Options de source des mots
  const sourceOptions = [
    { value: 'general', label: t('vocabulary.sourceGeneral') },
    { value: 'categories', label: t('vocabulary.sourceCategories') },
    { value: 'mes-playlists', label: t('vocabulary.sourceMesPlaylists') },
    { value: 'playlists-publiques', label: t('vocabulary.sourcePlaylistsPubliques') },
  ];

  // Options de niveau CECRL
  const niveauOptions = [
    { value: 'all', label: t('vocabulary.niveauAll') },
    { value: 'A1', label: t('vocabulary.niveauA1') },
    { value: 'A2', label: t('vocabulary.niveauA2') },
    { value: 'B1', label: t('vocabulary.niveauB1') },
    { value: 'B2', label: t('vocabulary.niveauB2') },
    { value: 'C1', label: t('vocabulary.niveauC1') },
    { value: 'C2', label: t('vocabulary.niveauC2') },
  ];

  // ========== EFFETS ==========

  // Sauvegarder les paramètres dans localStorage
  useEffect(() => {
    const params = {
      langueSource,
      langueCible,
      typeFilter,
      withArticle,
      checkAccents,
      sourceType,
      selectedPlaylistId,
      selectedCategorieCode,
      selectedThemeCode,
      niveauCecrl,
    };
    localStorage.setItem('vocabularyParams', JSON.stringify(params));
  }, [langueSource, langueCible, typeFilter, withArticle, checkAccents, sourceType, selectedPlaylistId, selectedCategorieCode, selectedThemeCode, niveauCecrl]);

  // Charger les playlists de l'utilisateur quand on sélectionne "mes playlists"
  useEffect(() => {
    if (sourceType === 'mes-playlists') {
      const fetchUserPlaylists = async () => {
        try {
          const response = await fetch(`/api/playlists?userId=${userId}`);
          const data = await response.json();
          if (data.success) {
            // Filtrer pour ne garder que les playlists de l'utilisateur (pas les publiques d'autres users)
            const myPlaylists = data.playlists.filter((p) => p.userId === userId);
            setUserPlaylists(myPlaylists);
            // Si aucune playlist sélectionnée, sélectionner la première
            if (!selectedPlaylistId && myPlaylists.length > 0) {
              setSelectedPlaylistId(myPlaylists[0].id);
            }
          }
        } catch (err) {
          console.error('Erreur chargement playlists:', err);
        }
      };
      fetchUserPlaylists();
    }
  }, [sourceType, selectedPlaylistId]);

  // Charger les catégories quand on sélectionne "categories"
  useEffect(() => {
    if (sourceType === 'categories' && categories.length === 0) {
      const fetchCategories = async () => {
        try {
          const response = await fetch('/api/categories');
          const data = await response.json();
          if (data.success) {
            setCategories(data.data);
          }
        } catch (err) {
          console.error('Erreur chargement catégories:', err);
        }
      };
      fetchCategories();
    }
  }, [sourceType, categories.length]);

  // Recherche de playlists publiques avec debounce
  useEffect(() => {
    if (sourceType !== 'playlists-publiques' || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/playlists/search?q=${encodeURIComponent(searchQuery)}`);
        const data = await response.json();
        if (data.success) {
          setSearchResults(data.playlists);
          setShowSearchDropdown(true);
        }
      } catch (err) {
        console.error('Erreur recherche playlists:', err);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [sourceType, searchQuery]);

  // Reset playlist/catégorie sélectionnée quand on change de source
  useEffect(() => {
    if (sourceType === 'general') {
      setSelectedPlaylistId(null);
      setSearchQuery('');
      setSearchResults([]);
    }
    if (sourceType !== 'categories') {
      setSelectedCategorieCode('');
      setSelectedThemeCode('');
    }
  }, [sourceType]);

  // ========== FONCTIONS PRINCIPALES ==========

  // Fonction pour mettre à jour le score d'un mot
  const updateMotScore = (motId, isCorrect) => {
    setMotScores((prev) => {
      const currentScore = prev[motId] || 0;
      const newScore = isCorrect ? currentScore + 1 : currentScore - 2;
      return { ...prev, [motId]: newScore };
    });
  };

  // Fonction pour ajouter une question à la file d'attente de retry
  const addToRetryQueue = (questionData) => {
    const questionsUntilRetry = Math.floor(Math.random() * 6) + 5; // 5-10 questions
    setRetryQueue((prev) => [
      ...prev,
      {
        motId: questionData.motId,
        questionsUntilRetry,
        questionData,
      },
    ]);
  };

  // Fonction pour décrémenter les compteurs de retry et récupérer une question prête
  const processRetryQueue = () => {
    let readyQuestion = null;

    setRetryQueue((prev) => {
      const updated = prev.map((item) => ({
        ...item,
        questionsUntilRetry: item.questionsUntilRetry - 1,
      }));

      // Trouver la première question prête (compteur <= 0)
      const readyIndex = updated.findIndex((item) => item.questionsUntilRetry <= 0);

      if (readyIndex !== -1) {
        readyQuestion = updated[readyIndex].questionData;
        // Retirer cette question de la queue
        return updated.filter((_, i) => i !== readyIndex);
      }

      return updated;
    });

    return readyQuestion;
  };

  // Charger une nouvelle question
  const loadNewQuestion = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setUserAnswer('');
    setShowHintModal(false);
    setShowDontKnowModal(false);
    setDontKnowAnswer('');

    // Si "Je ne sais pas" a été cliqué, reproposer le même mot
    if (retrySameWord && question) {
      setRetrySameWord(false);
      setLoading(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return;
    }

    try {
      // 1. Vérifier d'abord la file d'attente de retry
      const retryQuestion = processRetryQueue();

      if (retryQuestion) {
        // Utiliser la question de retry
        setQuestion(retryQuestion);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
        setLoading(false);
        return;
      }

      // 2. Sinon, charger une nouvelle question depuis l'API
      let url = `/api/exercices/vocabulaire/random?_t=${Date.now()}`;

      // Toujours envoyer langueSource et langueCible
      url += `&langueSource=${langueSource}`;
      url += `&langueCible=${langueCible}`;

      if (typeFilter !== 'all') {
        url += `&type=${typeFilter}`;
      }

      // Ajouter playlistId si une playlist est sélectionnée
      if (selectedPlaylistId && (sourceType === 'mes-playlists' || sourceType === 'playlists-publiques')) {
        url += `&playlistId=${selectedPlaylistId}`;
      }

      // Ajouter themeCode si catégories/thèmes sélectionné
      if (sourceType === 'categories') {
        if (selectedThemeCode) {
          url += `&themeCode=${selectedThemeCode}`;
        } else if (selectedCategorieCode) {
          url += `&themeCode=${selectedCategorieCode}`;
        }
      }

      // Ajouter niveau CECRL
      if (niveauCecrl && niveauCecrl !== 'all') {
        url += `&niveauCecrl=${niveauCecrl}`;
      }

      // Envoyer les IDs des mots déjà vus et leurs scores pour la sélection intelligente
      if (seenMotIds.size > 0) {
        url += `&excludeMotIds=${Array.from(seenMotIds).join(',')}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        // Si tous les mots ont été vus, réinitialiser et réessayer
        if (response.status === 404 && seenMotIds.size > 0) {
          setSeenMotIds(new Set());
          // Réessayer sans exclusion
          const retryResponse = await fetch(url.split('&excludeMotIds')[0]);
          const retryData = await retryResponse.json();
          if (retryData.success && retryData.data) {
            setQuestion(retryData.data);
            setSeenMotIds(new Set([retryData.data.motId]));
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
            return;
          }
        }
        throw new Error(data.error || data.message || 'Erreur lors du chargement');
      }

      if (data.success && data.data) {
        const newQuestion = data.data;
        setQuestion(newQuestion);

        // Ajouter aux mots vus
        setSeenMotIds((prev) => new Set([...prev, newQuestion.motId]));

        // Initialiser le score si pas encore vu
        if (!(newQuestion.motId in motScores)) {
          setMotScores((prev) => ({ ...prev, [newQuestion.motId]: 0 }));
        }

        // Focus sur l'input après chargement
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } else {
        throw new Error('Aucun mot trouvé');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier la réponse
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!userAnswer.trim()) return;
    setIsChecking(true);

    try {
      const response = await fetch('/api/exercices/vocabulaire/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reponseUtilisateur: userAnswer.trim(),
          withArticle: withArticle,
          checkAccents: checkAccents,
          reponsesAcceptees: question.reponsesAcceptees,
          langueCible: question.langueCible,
          typeMot: question.type, // Ajout du type de mot
          userId: user?.id,
        }),
      });

      if (response.status === 429) {
        const data = await response.json();
        setError(`Limite quotidienne atteinte (${data.limite} exercices/jour). Passez à un abonnement supérieur pour continuer.`);
        return;
      }

      const data = await response.json();

      if (data.success !== undefined) {
        setResult({
          correct: data.correct,
          correctAnswer: data.reponseCorrecte,
          userAnswer: userAnswer.trim(),
        });

        // Mettre à jour le score du mot
        updateMotScore(question.motId, data.correct);

        // Si incorrect, ajouter à la file d'attente de retry
        if (!data.correct) {
          addToRetryQueue(question);
        }

        setStats((prev) => ({
          correct: prev.correct + (data.correct ? 1 : 0),
          incorrect: prev.incorrect + (data.correct ? 0 : 1),
          total: prev.total + 1,
        }));
      }
    } catch (err) {
      console.error('Erreur vérification:', err);
      setError('Erreur lors de la vérification');
    } finally {
      setIsChecking(false);
    }
  };

  // Gérer "Je ne sais pas"
  const handleDontKnow = () => {
    if (!question || !question.reponsesAcceptees || question.reponsesAcceptees.length === 0) return;

    // Construire la réponse correcte (première réponse avec article si nécessaire)
    const premiereReponse = question.reponsesAcceptees[0];
    let reponseCorrecte = premiereReponse.mot;

    // N'ajouter l'article que pour les noms
    if (withArticle && question.type === 'nom') {
      const article = getArticleAvecElision(question.langueCible, premiereReponse.genre, 'singulier', premiereReponse.mot);
      const espace = article.endsWith("'") ? '' : ' ';
      reponseCorrecte = `${article}${espace}${premiereReponse.mot}`;
    }

    // Afficher la réponse dans une modale (sans bloquer le formulaire)
    setDontKnowAnswer(reponseCorrecte);
    setShowDontKnowModal(true);

    // Mettre à jour le score du mot (-2 pour incorrect)
    updateMotScore(question.motId, false);

    // Marquer pour reproposer le même mot au prochain "Mot suivant"
    setRetrySameWord(true);

    // Mettre à jour les stats
    setStats((prev) => ({
      correct: prev.correct,
      incorrect: prev.incorrect + 1,
      total: prev.total + 1,
    }));
  };

  // Fonction helper pour obtenir l'article
  // Alias pour compatibilité avec le code existant
  const getArticleAvecElision = getArticleDefini;

  // Démarrer l'exercice
  const startExercise = () => {
    // Réinitialiser le système de scoring pour la nouvelle session
    setMotScores({});
    setRetryQueue([]);
    setSeenMotIds(new Set());
    setRetrySameWord(false);
    setStats({ correct: 0, incorrect: 0, total: 0 });

    setShowExercise(true);
    loadNewQuestion();
    setTimeout(() => {
      exerciseRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  };

  // Retour aux paramètres
  const backToSettings = () => {
    setShowExercise(false);
    setQuestion(null);
    setResult(null);
    setStats({ correct: 0, incorrect: 0, total: 0 });
    // Réinitialiser le système de scoring
    setMotScores({});
    setRetryQueue([]);
    setSeenMotIds(new Set());
    setRetrySameWord(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Signaler un mot comme erroné
  const handleSignaler = async () => {
    if (!question || !question.motId) {
      console.error('Pas de mot à signaler');
      return;
    }

    const confirmation = window.confirm(t('vocabulary.confirmSignaler'));

    if (!confirmation) return;

    try {
      const response = await fetch(`/api/mots/${question.motId}/signaler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.success) {
        // Charger un nouveau mot directement sans popup de confirmation
        loadNewQuestion();
      } else {
        throw new Error(data.error || 'Erreur lors du signalement');
      }
    } catch (err) {
      console.error('Erreur signalement:', err);
      alert('❌ ' + t('vocabulary.erreurSignalement') + err.message);
    }
  };

  // Fonction pour insérer un caractère spécial - utilise la factory du module utilitaire
  const insertSpecialChar = createInsertSpecialChar(inputRef, userAnswer, setUserAnswer);

  // Calculer le pourcentage de réussite
  const successRate = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;

  // Fonction helper pour obtenir l'icône du type
  const getTypeIcon = (type) => {
    const typeIcons = {
      nom: '📦',
      adjectif: '✨',
      adverbe: '⚡',
      pronom: '👤',
      adjectif_indefini: '🔢',
    };
    return typeIcons[type] || '📚';
  };

  // ========== RENDU ==========
  return (
    <div className="vocabulary-exercise">
      {/* Écran des paramètres */}
      {!showExercise && (
        <div className="settings-screen">
          {!isEmbed && (
            <div className="exercise-header">
              <div className="header-title">
                <BookOpen size={32} color="#10B981" />
                <h2>{t('vocabulary.title')}</h2>
              </div>
            </div>
          )}
          <div className="settings-container">

            {/* Filtre Langue Source */}
            <div className="setting-item">
              <label>{t('vocabulary.langueSource')}</label>
              <select className="language-select" value={langueSource} onChange={(e) => setLangueSource(e.target.value)}>
                {langueSourceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.flag} {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre Langue Destination */}
            <div className="setting-item">
              <label>{t('vocabulary.langueDestination')}</label>
              <select className="language-select" value={langueCible} onChange={(e) => setLangueCible(e.target.value)}>
                {langueCibleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.flag} {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre Type de mot */}
            <div className="setting-item">
              <label>{t('vocabulary.typeMot')}</label>
              <select className="language-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Niveau CECRL */}
            <div className="setting-item">
              <label>{t('vocabulary.niveauCecrl')}</label>
              <select className="language-select" value={niveauCecrl} onChange={(e) => setNiveauCecrl(e.target.value)}>
                {niveauOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Source des mots */}
            <div className="setting-item">
              <label>{t('vocabulary.sourceMots')}</label>
              <select
                className="language-select"
                value={sourceType}
                onChange={(e) => {
                  setSourceType(e.target.value);
                  setSelectedPlaylistId(null);
                  setSearchQuery('');
                }}
              >
                {sourceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélecteur de playlist utilisateur */}
            {sourceType === 'mes-playlists' && (
              <div className="setting-item">
                <label>{t('vocabulary.choisirPlaylist')}</label>
                {userPlaylists.length > 0 ? (
                  <select className="language-select" value={selectedPlaylistId || ''} onChange={(e) => setSelectedPlaylistId(parseInt(e.target.value))}>
                    {userPlaylists.map((playlist) => (
                      <option key={playlist.id} value={playlist.id}>
                        {playlist.nom} ({t('vocabulary.motsCount', { count: playlist.motCount })})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="setting-hint">{t('vocabulary.pasDePlaylist')}</p>
                )}
              </div>
            )}

            {/* Recherche de playlists publiques */}
            {sourceType === 'playlists-publiques' && (
              <div className="setting-item">
                <label>{t('vocabulary.rechercherPlaylist')}</label>
                <div className="search-container" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="language-select"
                    placeholder={t('vocabulary.tapezPourRechercher')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setShowSearchDropdown(true)}
                    onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                  />
                  {showSearchDropdown && searchResults.length > 0 && (
                    <div
                      className="search-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 100,
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    >
                      {searchResults.map((playlist) => (
                        <div
                          key={playlist.id}
                          className="search-result-item"
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee',
                          }}
                          onMouseDown={() => {
                            setSelectedPlaylistId(playlist.id);
                            setSearchQuery(playlist.nom);
                            setShowSearchDropdown(false);
                          }}
                          onMouseEnter={(e) => (e.target.style.background = '#f5f5f5')}
                          onMouseLeave={(e) => (e.target.style.background = 'white')}
                        >
                          <div style={{ fontWeight: '500' }}>{playlist.nom}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {t('vocabulary.motsCountPar', { count: playlist.motCount, user: playlist.user })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedPlaylistId && sourceType === 'playlists-publiques' && (
                  <p className="setting-hint" style={{ color: '#10B981', marginTop: '8px' }}>
                    {t('vocabulary.playlistSelectionnee')}
                  </p>
                )}
              </div>
            )}

            {/* Sélecteurs catégorie / thème */}
            {sourceType === 'categories' && (
              <>
                <div className="setting-item">
                  <label>{t('vocabulary.categorie')}</label>
                  <select
                    className="language-select"
                    value={selectedCategorieCode}
                    onChange={(e) => {
                      setSelectedCategorieCode(e.target.value);
                      setSelectedThemeCode('');
                    }}
                  >
                    <option value="">{t('vocabulary.choisirCategorie')}</option>
                    {categories.map((cat) => (
                      <option key={cat.code} value={cat.code}>
                        {cat.categorie}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCategorieCode && (
                  <div className="setting-item">
                    <label>{t('vocabulary.theme')}</label>
                    <select
                      className="language-select"
                      value={selectedThemeCode}
                      onChange={(e) => setSelectedThemeCode(e.target.value)}
                    >
                      <option value="">{t('vocabulary.tousLesThemes')}</option>
                      {categories
                        .find((c) => c.code === selectedCategorieCode)
                        ?.themes?.map((theme) => (
                          <option key={theme.code} value={theme.code}>
                            {theme.theme}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* Option Avec article (seulement pour les noms) */}
            {(typeFilter === 'nom' || typeFilter === 'all') && (
              <div className="setting-item">
                <label className="checkbox-label">
                  <input type="checkbox" checked={withArticle} onChange={(e) => setWithArticle(e.target.checked)} />
                  {t('vocabulary.avecArticle')}
                </label>
                <p className="setting-hint">{t('vocabulary.avecArticleHint')}</p>
              </div>
            )}

            {/* Bouton Commencer */}
            <button onClick={startExercise} className="btn-start-exercise">
              <Play size={24} />
              {t('vocabulary.start')}
            </button>
          </div>
        </div>
      )}

      {/* Zone d'exercice */}
      {showExercise && (
        <>
          <div ref={exerciseRef} className="exercise-zone">
            {/* Erreur */}
            {error && (
              <div className="exercise-error">
                <XCircle size={24} color="#EF4444" />
                <div>
                  <p className="error-title">{t('vocabulary.erreur')}</p>
                  <p className="error-message">{error}</p>
                  <p className="error-hint">{t('vocabulary.erreurHint')}</p>
                </div>
              </div>
            )}

            {/* Chargement */}
            {loading && (
              <div className="exercise-loading">
                <Loader2 size={48} className="spin" color="#4F46E5" />
                <p>{t('vocabulary.chargementMot')}</p>
              </div>
            )}

            {/* Question */}
            {!loading && !error && question && (
              <div className="exercise-card">
                {/* En-tête de la question */}
                <div className="question-header">
                  <div className="question-context">
                    <span className="context-badge">
                      {getLangueFlag(question.langueSource)} → {getLangueFlag(question.langueCible)}
                    </span>
                    {question.type && (
                      <span className="context-badge type">
                        {getTypeIcon(question.type)} {question.type}
                      </span>
                    )}
                    {/* Pour les noms: bouton Indice au lieu d'afficher le genre */}
                    {question.type === 'nom' && question.genre && (
                      <button
                        onClick={() => setShowHintModal(true)}
                        className="btn-hint"
                        style={{
                          padding: '6px 16px',
                          backgroundColor: '#EEF2FF',
                          color: '#4F46E5',
                          border: '2px solid #C7D2FE',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#DDD6FE';
                          e.target.style.borderColor = '#A78BFA';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#EEF2FF';
                          e.target.style.borderColor = '#C7D2FE';
                        }}
                      >
                        {t('vocabulary.indice')}
                      </button>
                    )}
                    {/* Pour les autres types: afficher le genre normalement */}
                    {question.type !== 'nom' && question.genre && (
                      <span className="context-badge genre">
                        {question.genre === 'masculin' ? '♂️' : '♀️'} {t(`vocabulary.genre${question.genre.charAt(0).toUpperCase()}${question.genre.slice(1)}`)}
                      </span>
                    )}
                    {/* Score du mot */}
                    <span
                      className="context-badge score"
                      style={{
                        backgroundColor: (motScores[question.motId] || 0) >= 0 ? '#D1FAE5' : '#FEE2E2',
                        color: (motScores[question.motId] || 0) >= 0 ? '#065F46' : '#991B1B',
                        fontWeight: '600',
                      }}
                    >
                      📊 {(motScores[question.motId] || 0) >= 0 ? '+' : ''}
                      {motScores[question.motId] || 0}
                    </span>
                  </div>

                  <div className="word-to-translate">
                    <span className="source-word">
                      {withArticle &&
                        question.genreSource &&
                        question.type === 'nom' &&
                        (() => {
                          const article = getArticleAvecElision(question.langueSource, question.genreSource, 'singulier', question.motSource);
                          return (
                            <>
                              {article}
                              {article.endsWith("'") ? '' : ' '}
                            </>
                          );
                        })()}
                      {question.motSource}
                    </span>
                    {question.contexte && question.contexte !== '' && (
                      <div className="context-hint">{question.contexte}</div>
                    )}
                  </div>

                  {/* {withArticle && question.type === 'nom' && (
                    <p className="article-hint">
                      💡 N'oubliez pas l'article ({question.langueCible === 'fr' ? "le/la/l'/les" : question.langueCible === 'es' ? 'el/la/los/las' : 'the'})
                    </p>
                  )} */}
                </div>

                {/* Formulaire de réponse */}
                {!result && (
                  <>
                    <form onSubmit={handleSubmit} className="answer-form">
                      {/* Clavier virtuel avec checkbox "Vérifier les accents" */}
                      <VirtualKeyboard
                        langue={question.langueCible}
                        checkAccents={checkAccents}
                        onCheckAccentsChange={setCheckAccents}
                        onCharClick={insertSpecialChar}
                      />

                      <div className="answer-input-group">
                        <input
                          ref={inputRef}
                          type="text"
                          className="answer-input"
                          value={userAnswer}
                          onChange={(e) => setUserAnswer(e.target.value)}
                          placeholder={withArticle ? t('vocabulary.placeholderAvecArticle') : t('vocabulary.placeholderReponse')}
                          disabled={isChecking}
                          name={`answer-${Date.now()}`}
                          autoComplete="new-password"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          data-form-type="other"
                          data-lpignore="true"
                          data-1p-ignore="true"
                        />
                      </div>

                      {/* Bouton submit caché pour garder le Enter fonctionnel */}
                      <button type="submit" style={{ display: 'none' }} disabled={isChecking || !userAnswer.trim()} />
                    </form>

                    <div className="action-buttons-grid">
                      <button type="button" className="btn-verify" disabled={isChecking || !userAnswer.trim()} onClick={handleSubmit}>
                        {isChecking ? (
                          <>
                            <Loader2 size={20} className="spin" />
                            {t('vocabulary.verification')}
                          </>
                        ) : (
                          <>
                            <CheckCircle size={20} />
                            {t('vocabulary.verifier')}
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        className="btn-dont-know"
                        onClick={handleDontKnow}
                        disabled={isChecking}
                      >
                        {t('vocabulary.jeNeSaisPas')}
                      </button>

                      {question && !question.signale && (
                        <button
                          type="button"
                          onClick={handleSignaler}
                          className="btn-signaler"
                        >
                          {t('vocabulary.signalerErreur')}
                        </button>
                      )}

                      <button type="button" onClick={backToSettings} className="btn-back-settings">
                        {t('vocabulary.parametres')}
                      </button>
                    </div>
                  </>
                )}

                {/* Résultat */}
                {result && (
                  <div className="result-section">
                    <div className={`result-box ${result.correct ? 'correct' : 'incorrect'}`}>
                      <div className="result-icon">{result.correct ? <CheckCircle size={32} color="#10B981" /> : <XCircle size={32} color="#EF4444" />}</div>
                      <div className="result-content">
                        <p className="result-title">{result.correct ? t('vocabulary.correct') : t('vocabulary.incorrect')}</p>
                        {!result.correct && (
                          <div className="result-details">
                            <p className="your-answer">
                              {t('vocabulary.votreReponse')} : <strong>{result.userAnswer}</strong>
                            </p>
                            <p className="correct-answer">
                              {t('vocabulary.reponseAttendue')} : <strong>{result.correctAnswer}</strong>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats compactes */}
                    {stats.total > 0 && (
                      <div className="stats-compact">
                        <span className="stat-compact">
                          <CheckCircle size={16} color="#10B981" />
                          {stats.correct}
                        </span>
                        <span className="stat-compact">
                          <XCircle size={16} color="#EF4444" />
                          {stats.incorrect}
                        </span>
                        <span className="stat-compact">
                          <TrendingUp size={16} color="#4F46E5" />
                          {successRate}%
                        </span>
                        {retryQueue.length > 0 && (
                          <span className="stat-compact" title="Questions à revoir">
                            🔄 {retryQueue.length}
                          </span>
                        )}
                      </div>
                    )}

                    <button onClick={loadNewQuestion} className="btn-next">
                      <RotateCw size={20} />
                      {t('vocabulary.retourQuestion')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Boutons de bas de page (quand résultat affiché) */}
          {result && (
            <div className="action-buttons-grid" style={{ marginTop: '16px' }}>
              {question && !question.signale && (
                <button
                  type="button"
                  onClick={handleSignaler}
                  className="btn-signaler"
                >
                  {t('vocabulary.signalerErreur')}
                </button>
              )}

              <button type="button" onClick={backToSettings} className="btn-back-settings">
                {t('vocabulary.parametres')}
              </button>
            </div>
          )}
        </>
      )}

      {/* Modale Indice Genre */}
      {showHintModal && question && question.genre && (
        <div
          className="modal-overlay"
          onClick={() => setShowHintModal(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: '32px', maxWidth: '400px', textAlign: 'center' }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>{question.genre === 'masculin' ? '♂️' : '♀️'}</div>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>{t('vocabulary.reponseGenre', { genre: t(`vocabulary.genre${question.genre.charAt(0).toUpperCase()}${question.genre.slice(1)}`) })}</p>
            <button
              onClick={() => setShowHintModal(false)}
              style={{
                marginTop: '24px',
                padding: '10px 24px',
                backgroundColor: '#4F46E5',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#4338CA';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#4F46E5';
              }}
            >
              {t('vocabulary.fermer')}
            </button>
          </div>
        </div>
      )}

      {/* Modale "Je ne sais pas" */}
      {showDontKnowModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowDontKnowModal(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: '32px', maxWidth: '400px', textAlign: 'center' }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💡</div>
            <p style={{ fontSize: '16px', color: '#6B7280', marginBottom: '8px' }}>{t('vocabulary.reponseCorrecteEtait')}</p>
            <p style={{ fontSize: '22px', fontWeight: '700', color: '#4F46E5', marginBottom: '16px' }}>{dontKnowAnswer}</p>
            <p style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '24px' }}>{t('vocabulary.motReproposera')}</p>
            <button
              onClick={() => setShowDontKnowModal(false)}
              style={{
                padding: '10px 24px',
                backgroundColor: '#4F46E5',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
              }}
            >
              {t('vocabulary.continuer')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
